import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SharingContext } from './session.service';

/**
 * Session State Service
 * Simple in-memory state management for session data
 * No browser storage, no URL tricks - just Angular app state
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStateService {
  // Private BehaviorSubjects - pure in-memory state
  private sharingContextSubject = new BehaviorSubject<SharingContext | null>(null);
  private sessionIdSubject = new BehaviorSubject<string | null>(null);

  // Public observables
  public sharingContext$: Observable<SharingContext | null> = this.sharingContextSubject.asObservable();
  public sessionId$: Observable<string | null> = this.sessionIdSubject.asObservable();

  /**
   * Set sharing context (in-memory only)
   */
  setSharingContext(context: SharingContext | null): void {
    this.sharingContextSubject.next(context);
  }

  /**
   * Get current sharing context
   */
  getSharingContext(): SharingContext | null {
    return this.sharingContextSubject.value;
  }

  /**
   * Set session ID (in-memory only)
   */
  setSessionId(sessionId: string | null): void {
    this.sessionIdSubject.next(sessionId);
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionIdSubject.value;
  }

  /**
   * Clear all state
   */
  clearSessionState(): void {
    this.sharingContextSubject.next(null);
    this.sessionIdSubject.next(null);
  }

  /**
   * Check if session exists
   */
  hasActiveSession(): boolean {
    return this.sessionIdSubject.value !== null;
  }

  /**
   * Check if shared session
   */
  isSharedSession(): boolean {
    return this.sharingContextSubject.value?.isShared ?? false;
  }

  /**
   * Check if view-only
   */
  isViewOnlySession(): boolean {
    return this.sharingContextSubject.value?.isViewOnly ?? false;
  }

  /**
   * Get user role
   */
  getUserRole(): 'owner' | 'recipient' | null {
    return this.sharingContextSubject.value?.userRole ?? null;
  }
}
